// Formatting + client-side deterministic calc/verdict parity (instant feedback).
// Mirrors backend app/calc.py exactly; server result is authoritative on reconciliation.

export const usd = (n, dash = "—") => {
  if (n === null || n === undefined || Number.isNaN(n)) return dash;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

/**
 * Currency-aware money formatter: respects the persisted currency code instead
 * of silently formatting every number as USD. Falls back to "—" for null/NaN.
 */
export const money = (n, currency = "USD", dash = "—") => {
  if (n === null || n === undefined || Number.isNaN(n)) return dash;
  const code = String(currency || "USD").toUpperCase() || "USD";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(n);
  } catch {
    // Unknown/unsupported currency code: never guess a symbol.
    return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n)} ${code}`;
  }
};

// Stripe-style compact currency for constrained KPI/summary displays only.
// Compact notation begins at |value| >= 10,000. Full/exact values must be used everywhere else.
export function usdCompact(value, currency = "USD") {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: Math.abs(value) >= 10000 ? "compact" : "standard",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

// Exact currency (always full, 2 decimals) for tooltips, tables, exports, detail views.
export function usdExact(value, currency = "USD") {
  if (value === null || value === undefined || !Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
export const pct = (n, dash = "—") => {
  if (n === null || n === undefined || Number.isNaN(n)) return dash;
  return `${n.toFixed(1)}%`;
};
export const num = (n) => new Intl.NumberFormat("en-US").format(n ?? 0);
export const ago = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const cents = (d) => {
  if (d === null || d === undefined || d === "") return 0;
  const n = Number(d);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};
const dollars = (c) => c / 100;

const TARGET_ROI = 30, MIN_ROI = 20, TARGET_PROFIT = 300, STRONG_ROI = 45, STRONG_CONF = 80, SAFE_CONF = 55;

/** @param {any} p */
export function computeCalc(p) {
  const rate = p.referral_rate ?? 0.15;
  const ratePresent = p.referral_rate !== null && p.referral_rate !== undefined;
  const acquisition = cents(p.acquisition_cost);
  const inbound = cents(p.inbound_shipping);
  const prep = cents(p.prep_label);
  const misc = cents(p.misc);
  const storage = cents(p.storage);
  const wfs = cents(p.wfs_fee);
  const sale = cents(p.sale_price);
  const qty = Math.max(1, Math.round(Number(p.quantity) || 1));

  const saleMissing = sale <= 0;
  const acqMissing = acquisition <= 0;
  const inputsComplete = !saleMissing && !acqMissing && ratePresent;

  let ruleSource =
    p.rule_source ||
    p.acquisition_cost_provenance ||
    (p.data_mode === "live" ? "live" : "fixture");
  if (ruleSource === "missing") ruleSource = p.data_mode === "live" ? "live" : "fixture";

  const acqProvenance =
    p.acquisition_cost_provenance === "price_index"
      ? "price_index"
      : acqMissing
        ? "missing"
        : p.acquisition_cost_provenance || (ruleSource === "fixture" ? "fixture" : "user-entered");

  const referral = Math.round(sale * rate);
  const walmartFees = referral + wfs;
  const logistics = inbound + prep + storage;
  const landed = acquisition + inbound + prep + storage + misc;
  const payout = sale - referral - wfs;
  const netProfitRaw = payout - landed;

  const netProfit = inputsComplete ? netProfitRaw : null;
  const roi = inputsComplete && acquisition > 0 ? (netProfit / acquisition) * 100 : null;
  const margin = inputsComplete && sale > 0 ? (netProfit / sale) * 100 : null;

  const fixedNonReferral = wfs + storage + inbound + prep + misc + acquisition;
  const breakEven =
    inputsComplete && 1 - rate > 0 ? Math.round(fixedNonReferral / (1 - rate)) : null;

  const fixedNonAcq = referral + wfs + storage + inbound + prep + misc;
  const K = sale - fixedNonAcq;
  const r = TARGET_ROI / 100;
  const roiCeiling = Math.round(K / (1 + r));
  const profitCeiling = K - TARGET_PROFIT;
  const maxAcq = Math.min(profitCeiling, roiCeiling);
  const acqGap = inputsComplete ? maxAcq - acquisition : null;

  const breakdown = [
    {
      label: "Sale price",
      kind: "income",
      amount: dollars(sale),
      provenance: ruleSource === "fixture" ? "user-entered" : "walmart_catalog",
    },
    { label: "Referral fee", sub: `${Math.round(rate * 100)}% of sale`, kind: "expense", amount: dollars(referral), provenance: "calculated" },
    { label: "WFS fulfillment fee", kind: "expense", amount: dollars(wfs), provenance: "user-entered" },
    { label: "Inbound shipping", kind: "expense", amount: dollars(inbound), provenance: "user-entered" },
    { label: "Prep & labeling", kind: "expense", amount: dollars(prep), provenance: "user-entered" },
    { label: "Storage estimate", kind: "expense", amount: dollars(storage), provenance: "estimated" },
    { label: "Miscellaneous", kind: "expense", amount: dollars(misc), provenance: "user-entered" },
    {
      label: "Acquisition cost",
      kind: "expense",
      amount: acquisition > 0 ? dollars(acquisition) : null,
      provenance: acqProvenance,
      ...(p.acquisition_cost_source ? { source_site: p.acquisition_cost_source } : {}),
    },
    {
      label: "Net profit",
      kind: "total",
      amount: netProfit === null ? null : dollars(netProfit),
      provenance: inputsComplete ? "calculated" : "incomplete",
    },
  ];

  return {
    breakdown,
    formula_version: "calc-2026.06.1",
    inputs_complete: inputsComplete,
    sale_price: sale > 0 ? dollars(sale) : null,
    landed_cost: inputsComplete ? dollars(landed) : null,
    marketplace_fees: sale > 0 ? dollars(walmartFees) : null,
    total_walmart_fees: sale > 0 ? dollars(walmartFees) : null,
    total_logistics_costs: dollars(logistics),
    estimated_walmart_payout: sale > 0 ? dollars(payout) : null,
    referral_fee: sale > 0 ? dollars(referral) : null,
    net_profit: netProfit === null ? null : dollars(netProfit),
    total_net_profit: netProfit === null ? null : dollars(netProfit * qty),
    roi_percent: roi === null ? null : Math.round(roi * 10) / 10,
    margin_percent: margin === null ? null : Math.round(margin * 10) / 10,
    break_even_price: breakEven === null ? null : dollars(breakEven),
    acquisition_cost: acquisition > 0 ? dollars(acquisition) : 0,
    acquisition_cost_provenance: acqProvenance,
    acquisition_cost_source: p.acquisition_cost_source || null,
    maximum_acquisition_cost: sale > 0 ? dollars(maxAcq) : null,
    acquisition_gap: acqGap === null ? null : dollars(acqGap),
    target_roi: TARGET_ROI,
    quantity: qty,
  };
}

/**
 * Hypothetical ROI / break-even sale prices from acquisition + fee assumptions.
 * Labeled scenarios only — never presented as observed historical prices.
 * @param {any} input
 * @param {number[]} targets
 */
export function computeRoiScenarios(input, targets = [20, 30, 45, 60]) {
  const acq = Number(input?.acquisition_cost);
  const rate = Number(input?.referral_rate);
  const wfs = Number(input?.wfs_fee) || 0;
  const inbound = Number(input?.inbound_shipping) || 0;
  const prep = Number(input?.prep_label) || 0;
  const storage = Number(input?.storage) || 0;
  const misc = Number(input?.misc) || 0;
  const sale = Number(input?.sale_price);

  const missing = [];
  if (!(acq > 0)) missing.push("acquisition cost");
  if (!Number.isFinite(rate) || rate < 0) missing.push("referral rate");
  if (missing.length) {
    return { available: false, missing, rows: [], break_even_price: null };
  }

  const fixed = wfs + inbound + prep + storage + misc;
  const denom = 1 - rate;
  if (!(denom > 0)) {
    return { available: false, missing: ["referral rate"], rows: [], break_even_price: null };
  }

  const breakEvenCents = Math.round((acq + fixed) / denom * 100) / 100;

  const rows = targets.map((targetRoi) => {
    const t = Number(targetRoi) / 100;
    const requiredSale = Math.round(((acq + fixed + acq * t) / denom) * 100) / 100;
    const referral = Math.round(requiredSale * rate * 100) / 100;
    const expectedProfit = Math.round((requiredSale - referral - wfs - inbound - prep - storage - misc - acq) * 100) / 100;
    const maxAcqForSale =
      Number.isFinite(sale) && sale > 0
        ? (() => {
            const saleRef = Math.round(sale * rate * 100) / 100;
            const K = sale - saleRef - wfs - storage - inbound - prep - misc;
            const roiCeiling = Math.round((K / (1 + t)) * 100) / 100;
            const profitCeiling = Math.round((K - 3) * 100) / 100;
            return Math.min(profitCeiling, roiCeiling);
          })()
        : null;
    return {
      target_roi: targetRoi,
      required_sale: requiredSale,
      max_acquisition: maxAcqForSale,
      expected_profit: expectedProfit,
      feasible: Number.isFinite(sale) && sale > 0 ? sale >= requiredSale : null,
      scenario: true,
    };
  });

  return {
    available: true,
    missing: [],
    break_even_price: breakEvenCents,
    rows,
  };
}

/** @param {any} input @param {any} calc */
export function computeVerdict(input, calc) {
  const conf = input.confidence ?? 82;
  const matchOk = ["exact", "probable"].includes(input.match_status);
  const walmart = input.walmart_selling !== false;
  const obs = input.observation_count ?? 120;
  const eligible = !!input.eligibility_verified;

  let ruleSource =
    input.rule_source ||
    input.acquisition_cost_provenance ||
    (input.data_mode === "live" ? "live" : "fixture");
  if (ruleSource === "missing" || ruleSource === "incomplete") {
    ruleSource = input.data_mode === "live" ? "live" : "fixture";
  }

  const r = (name, threshold, observed, status, affects) => {
    const rule = {
      name,
      threshold,
      observed,
      status,
      affects,
      source: ruleSource,
    };
    if (ruleSource === "fixture") rule.fixture = true;
    return rule;
  };

  const sale = Number(input.sale_price);
  const acq = Number(input.acquisition_cost);
  const saleOk = Number.isFinite(sale) && sale > 0;
  const acqOk = Number.isFinite(acq) && acq > 0;
  const rateOk = input.referral_rate !== null && input.referral_rate !== undefined;
  const missing = [];
  if (!saleOk) missing.push("selling price");
  if (!acqOk) missing.push("acquisition cost");
  if (!rateOk) missing.push("referral rate");

  if (input.match_status === "conflict") {
    return {
      verdict: "INCOMPLETE",
      headline: "Identifier / pack conflict blocks the verdict.",
      rules: [r("Exact product match", "exact/probable", "conflict", "fail", true)],
      counts: { pass: 0, warning: 0, fail: 1, unknown: 0 },
    };
  }
  if (missing.length || calc.inputs_complete === false || calc.net_profit === null) {
    return {
      verdict: "INCOMPLETE",
      headline: `Missing required input: ${missing.join(", ") || "required fields"}.`,
      rules: [r("Required inputs present", "all", missing.join(", ") || "incomplete", "fail", true)],
      counts: { pass: 0, warning: 0, fail: Math.max(1, missing.length), unknown: 0 },
    };
  }

  const roi = calc.roi_percent ?? 0;
  const rules = [
    r("Exact product match", "exact/probable", input.match_status, matchOk ? "pass" : "fail", true),
    r("Net profit positive", "> $0", usd(calc.net_profit), calc.net_profit > 0 ? "pass" : "fail", true),
    r(`ROI ≥ ${MIN_ROI}% (blocking)`, `≥ ${MIN_ROI}%`, pct(roi), roi >= MIN_ROI ? "pass" : "fail", true),
    r("Acquisition ≤ maximum", `≤ ${usd(calc.maximum_acquisition_cost)}`, usd(calc.acquisition_cost), calc.acquisition_cost <= calc.maximum_acquisition_cost ? "pass" : "fail", true),
    r("Match confidence safe", `≥ ${SAFE_CONF}%`, `${conf}%`, conf >= SAFE_CONF ? "pass" : "fail", true),
    r("Net profit ≥ target", "≥ $3.00", usd(calc.net_profit), calc.net_profit >= TARGET_PROFIT / 100 ? "pass" : "warning", false),
    r("Walmart not competing", "Walmart absent", walmart ? "Walmart selling" : "absent", walmart ? "warning" : "pass", true),
    r("Confidence ≥ strong threshold", `≥ ${STRONG_CONF}%`, `${conf}%`, conf >= STRONG_CONF ? "pass" : "warning", false),
    r("Historical coverage adequate", "≥ 30 obs", `${obs} obs`, obs >= 30 ? "pass" : "warning", false),
    r("Eligibility verified", "verified", eligible ? "verified" : "unverified", eligible ? "pass" : "unknown", false),
  ];
  const counts = rules.reduce((a, x) => ((a[x.status] = (a[x.status] || 0) + 1), a), {});
  const profitPasses = rules.filter((x) => x.status === "pass" && ["Net profit positive", `ROI ≥ ${MIN_ROI}% (blocking)`, "Acquisition ≤ maximum", "Match confidence safe", "Net profit ≥ target", "Exact product match"].includes(x.name)).length;

  const blockingFail = rules.some((x) => x.affects && x.status === "fail");
  const profitPass = calc.net_profit > 0 && roi >= MIN_ROI;
  if (blockingFail || !profitPass) return { verdict: "AVOID", headline: `Blocking rule failed — ROI ${pct(roi)} / profit ${usd(calc.net_profit)}.`, rules, counts };
  if (walmart || conf < STRONG_CONF || obs < 30 || !eligible)
    return { verdict: "REVIEW", headline: `${profitPasses} profitability rules passed. ${walmart ? "1 competition warning requires review" : "requires review"}.`, rules, counts };
  if (roi >= STRONG_ROI && calc.net_profit >= TARGET_PROFIT / 100 && conf >= STRONG_CONF)
    return { verdict: "STRONG BUY", headline: `All blocking rules pass — ${pct(roi)} ROI, ${usd(calc.acquisition_gap)} under maximum acquisition, High confidence.`, rules, counts };
  return { verdict: "BUY", headline: `Blocking rules pass at ${pct(roi)} ROI; only limited warnings remain.`, rules, counts };
}
